import clsx from 'clsx'
import {
  ChevronFirst,
  ChevronLast,
  Loader2,
  Maximize2,
  Minimize2,
  Pause,
  Play,
  SkipBack,
  SkipForward,
} from 'lucide-react'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/components/ui/button'
import { resolveMediaDisplayUrl } from '@/features/studio/lib/media-display'
import {
  frameIndexAt,
  formatReviewTimecode,
  REVIEW_DEFAULT_FPS,
  REVIEW_TIMELINE_MIN_WINDOW_SEC,
  REVIEW_TIMELINE_WINDOW_SEC,
  secToMs,
} from '@/features/studio/pages/reviews/review-timecode'

const VIDEO_ZOOM_LEVELS = [1, 1.25, 1.5, 2, 3, 4] as const

export type ReviewCommentMarker = {
  id: string
  time_start_ms?: number | null
  time_end_ms?: number | null
  resolved?: boolean
}

export type ReviewAnnotationRange = {
  time_start_ms: number
  time_end_ms: number
  fps_snapshot: number
}

export type ReviewVideoPlayerHandle = {
  getAnnotationRange(): ReviewAnnotationRange | null
  seekToMs(ms: number): void
  clearInOut(): void
}

type ReviewVideoPlayerProps = {
  src: string
  durationHintSec?: number
  markers?: ReviewCommentMarker[]
  onMarkerClick?: (marker: ReviewCommentMarker) => void
  onInOutChange?: (range: { in: number | null; out: number | null }) => void
  className?: string
}

export const ReviewVideoPlayer = forwardRef<ReviewVideoPlayerHandle, ReviewVideoPlayerProps>(
  function ReviewVideoPlayer(
    { src, durationHintSec = 0, markers = [], onMarkerClick, onInOutChange, className },
    ref,
  ) {
    const { t } = useTranslation()
    const fps = REVIEW_DEFAULT_FPS
    const videoRef = useRef<HTMLVideoElement>(null)
    const timelineRef = useRef<HTMLDivElement>(null)
    const stageRef = useRef<HTMLDivElement>(null)
    const stepLockRef = useRef(false)
    const panDragRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null)

    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(Math.max(0, durationHintSec))
    const [playing, setPlaying] = useState(false)
    const [seeking, setSeeking] = useState(false)
    const [ready, setReady] = useState(false)
    const [inPoint, setInPoint] = useState<number | null>(null)
    const [outPoint, setOutPoint] = useState<number | null>(null)
    const [windowStart, setWindowStart] = useState(0)
    const [windowDuration, setWindowDuration] = useState(REVIEW_TIMELINE_WINDOW_SEC)
    const [videoZoomIdx, setVideoZoomIdx] = useState(0)
    const [pan, setPan] = useState({ x: 0, y: 0 })

    const videoZoom = VIDEO_ZOOM_LEVELS[videoZoomIdx] ?? 1

    const displayUrl = resolveMediaDisplayUrl(src)
    const frameDur = 1 / fps
    const frameEps = frameDur / 2

    const clampTime = useCallback(
      (sec: number) => {
        const max = duration > 0 ? Math.max(0, duration - frameEps) : sec
        return Math.max(0, Math.min(max, sec))
      },
      [duration, frameEps],
    )

    const seekTo = useCallback(
      (sec: number) => {
        const v = videoRef.current
        if (!v) return
        const t = clampTime(sec)
        setSeeking(true)
        v.currentTime = t
        setCurrentTime(t)
      },
      [clampTime],
    )

    const seekToMs = useCallback(
      (ms: number) => {
        seekTo(ms / 1000)
      },
      [seekTo],
    )

    const ensureWindowContains = useCallback(
      (sec: number, dur: number) => {
        const win = Math.min(windowDuration, dur || windowDuration)
        setWindowDuration(win)
        setWindowStart((prev) => {
          if (sec < prev) return Math.max(0, sec - win * 0.1)
          if (sec > prev + win) return Math.max(0, sec - win * 0.85)
          return prev
        })
      },
      [windowDuration],
    )

    const stepFrame = useCallback(
      (delta: 1 | -1) => {
        const v = videoRef.current
        if (!v || stepLockRef.current) return
        stepLockRef.current = true
        v.pause()
        seekTo(v.currentTime + delta * frameDur)
        window.setTimeout(() => {
          stepLockRef.current = false
        }, 40)
      },
      [frameDur, seekTo],
    )

    const togglePlay = useCallback(() => {
      const v = videoRef.current
      if (!v) return
      if (v.paused) {
        void v.play().catch(() => undefined)
      } else {
        v.pause()
      }
    }, [])

    const setInAtCurrent = useCallback(() => {
      setInPoint(currentTime)
      setOutPoint((prev) => (prev != null && prev < currentTime ? currentTime : prev))
    }, [currentTime])

    const setOutAtCurrent = useCallback(() => {
      setOutPoint(currentTime)
      setInPoint((prev) => (prev != null && prev > currentTime ? currentTime : prev))
    }, [currentTime])

    const clearInOut = useCallback(() => {
      setInPoint(null)
      setOutPoint(null)
    }, [])

    const getAnnotationRange = useCallback((): ReviewAnnotationRange | null => {
      const v = videoRef.current
      if (!v || !Number.isFinite(v.duration)) return null

      const point = (sec: number) => {
        const t = clampTime(sec)
        const ms = secToMs(t)
        return { time_start_ms: ms, time_end_ms: ms, fps_snapshot: fps }
      }

      if (inPoint != null && outPoint != null) {
        const a = Math.min(inPoint, outPoint)
        const b = Math.max(inPoint, outPoint)
        return { time_start_ms: secToMs(a), time_end_ms: secToMs(b), fps_snapshot: fps }
      }
      if (inPoint != null) return point(inPoint)
      if (outPoint != null) return point(outPoint)
      return point(v.currentTime)
    }, [clampTime, fps, inPoint, outPoint])

    useImperativeHandle(
      ref,
      () => ({
        getAnnotationRange,
        seekToMs,
        clearInOut,
      }),
      [clearInOut, getAnnotationRange, seekToMs],
    )

    useEffect(() => {
      onInOutChange?.({ in: inPoint, out: outPoint })
    }, [inPoint, outPoint, onInOutChange])

    useEffect(() => {
      setCurrentTime(0)
      setPlaying(false)
      setSeeking(false)
      setReady(false)
      clearInOut()
      setWindowStart(0)
      setVideoZoomIdx(0)
      setPan({ x: 0, y: 0 })
      const hint = Math.max(0, durationHintSec)
      setDuration(hint)
      setWindowDuration(
        hint > 0
          ? Math.min(REVIEW_TIMELINE_WINDOW_SEC, Math.max(REVIEW_TIMELINE_MIN_WINDOW_SEC, hint))
          : REVIEW_TIMELINE_WINDOW_SEC,
      )
    }, [src, durationHintSec, clearInOut])

    useEffect(() => {
      if (duration <= 0) return
      setWindowDuration((prev) => Math.min(prev, duration))
      ensureWindowContains(currentTime, duration)
    }, [duration, currentTime, ensureWindowContains])

    useEffect(() => {
      const onKey = (e: KeyboardEvent) => {
        const tag = (e.target as HTMLElement | null)?.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA') return
        if (e.key === ' ') {
          e.preventDefault()
          togglePlay()
        } else if (e.key === 'ArrowLeft' && !e.shiftKey) {
          e.preventDefault()
          stepFrame(-1)
        } else if (e.key === 'ArrowRight' && !e.shiftKey) {
          e.preventDefault()
          stepFrame(1)
        } else if (e.key === 'ArrowLeft' && e.shiftKey) {
          e.preventDefault()
          seekTo(currentTime - 1)
        } else if (e.key === 'ArrowRight' && e.shiftKey) {
          e.preventDefault()
          seekTo(currentTime + 1)
        } else if (e.key.toLowerCase() === 'i') {
          e.preventDefault()
          setInAtCurrent()
        } else if (e.key.toLowerCase() === 'o') {
          e.preventDefault()
          setOutAtCurrent()
        }
      }
      window.addEventListener('keydown', onKey)
      return () => window.removeEventListener('keydown', onKey)
    }, [currentTime, seekTo, setInAtCurrent, setOutAtCurrent, stepFrame, togglePlay])

    const winDur = duration > 0 ? Math.min(windowDuration, duration) : windowDuration
    const winEnd = Math.min(duration || winDur, windowStart + winDur)
    const playheadRatio = winDur > 0 ? (currentTime - windowStart) / winDur : 0

    const zoomTimeline = (factor: number) => {
      if (duration <= 0) return
      setWindowDuration((prev) => {
        const next = Math.min(duration, Math.max(REVIEW_TIMELINE_MIN_WINDOW_SEC, prev * factor))
        return next
      })
    }

    const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const el = timelineRef.current
      if (!el || winDur <= 0) return
      const rect = el.getBoundingClientRect()
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      seekTo(windowStart + ratio * winDur)
    }

    const handleTimelineWheel = (e: React.WheelEvent<HTMLDivElement>) => {
      e.preventDefault()
      const factor = e.deltaY > 0 ? 1.25 : 0.8
      zoomTimeline(factor)
    }

    const zoomVideo = (delta: 1 | -1) => {
      setVideoZoomIdx((idx) => {
        const next = Math.max(0, Math.min(VIDEO_ZOOM_LEVELS.length - 1, idx + delta))
        if (next === 0) setPan({ x: 0, y: 0 })
        return next
      })
    }

    const onStagePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
      if (videoZoom <= 1) return
      panDragRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y }
      e.currentTarget.setPointerCapture(e.pointerId)
    }

    const onStagePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
      const drag = panDragRef.current
      if (!drag) return
      setPan({
        x: drag.panX + (e.clientX - drag.x),
        y: drag.panY + (e.clientY - drag.y),
      })
    }

    const onStagePointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
      panDragRef.current = null
      e.currentTarget.releasePointerCapture(e.pointerId)
    }

    const onStageWheel = (e: React.WheelEvent<HTMLDivElement>) => {
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()
      zoomVideo(e.deltaY > 0 ? -1 : 1)
    }

    return (
      <div className={clsx('review-video-player flex h-full min-h-0 w-full flex-col gap-1.5', className)}>
        <div
          ref={stageRef}
          className={clsx(
            'review-video-player__stage relative flex min-h-0 flex-1 items-center justify-center overflow-hidden',
            videoZoom > 1 ? 'cursor-grab active:cursor-grabbing' : '',
          )}
          onPointerDown={onStagePointerDown}
          onPointerMove={onStagePointerMove}
          onPointerUp={onStagePointerUp}
          onPointerCancel={onStagePointerUp}
          onWheel={onStageWheel}
        >
          <div
            className="review-video-player__frame flex max-h-full max-w-full items-center justify-center"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${videoZoom})`,
              transformOrigin: 'center center',
            }}
          >
            <video
              ref={videoRef}
              src={displayUrl}
              preload="metadata"
              playsInline
              draggable={false}
              className="max-h-full max-w-full rounded-lg bg-black object-contain shadow-lg ring-1 ring-white/10"
            onLoadedMetadata={(e) => {
              const d = e.currentTarget.duration
              if (Number.isFinite(d) && d > 0) {
                setDuration(d)
                setWindowDuration(Math.min(REVIEW_TIMELINE_WINDOW_SEC, Math.max(REVIEW_TIMELINE_MIN_WINDOW_SEC, d)))
              }
              setReady(true)
            }}
            onTimeUpdate={(e) => {
              const t = e.currentTarget.currentTime
              setCurrentTime(t)
              if (duration > 0) ensureWindowContains(t, duration)
            }}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onSeeking={() => setSeeking(true)}
            onSeeked={(e) => {
              setSeeking(false)
              setCurrentTime(e.currentTarget.currentTime)
            }}
          />
          </div>
          {seeking ? (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-amber-300/80" />
            </div>
          ) : null}
        </div>

        <div className="review-video-player__hud flex flex-wrap items-center justify-between gap-2 px-1 font-mono text-[11px] text-zinc-400">
          <span>
            {formatReviewTimecode(currentTime, fps)}
            <span className="mx-1.5 text-zinc-600">·</span>
            F{frameIndexAt(currentTime, fps)}
          </span>
          <span className="text-zinc-600">
            {formatReviewTimecode(duration, fps)}
            {duration > 0 ? ` (${Math.round(duration)}s)` : ''}
            {videoZoom > 1 ? (
              <span className="ml-2 text-amber-300/80">{Math.round(videoZoom * 100)}%</span>
            ) : null}
          </span>
        </div>

        <div
          ref={timelineRef}
          role="slider"
          aria-label={t('reviews.videoTimeline')}
          aria-valuemin={0}
          aria-valuemax={duration}
          aria-valuenow={currentTime}
          tabIndex={0}
          className="review-video-player__timeline relative h-9 cursor-pointer rounded-lg bg-[#12121a] ring-1 ring-amber-400/25"
          onClick={handleTimelineClick}
          onWheel={handleTimelineWheel}
        >
          {duration > 0 && winDur > 0 ? (
            <>
              <div
                className="absolute inset-y-1 rounded-md bg-amber-500/10"
                style={{
                  left: `${Math.max(0, ((windowStart - windowStart) / winDur) * 100)}%`,
                  width: '100%',
                }}
              />
              {inPoint != null ? (
                <div
                  className="absolute inset-y-1 z-[5] w-0.5 -translate-x-1/2 bg-indigo-400"
                  style={{
                    left: `${Math.max(0, Math.min(100, ((inPoint - windowStart) / winDur) * 100))}%`,
                  }}
                  title={`In ${formatReviewTimecode(inPoint, fps)}`}
                />
              ) : null}
              {outPoint != null ? (
                <div
                  className="absolute inset-y-1 z-[5] w-0.5 -translate-x-1/2 bg-violet-400"
                  style={{
                    left: `${Math.max(0, Math.min(100, ((outPoint - windowStart) / winDur) * 100))}%`,
                  }}
                  title={`Out ${formatReviewTimecode(outPoint, fps)}`}
                />
              ) : null}
              {inPoint != null && outPoint != null ? (
                <div
                  className="absolute inset-y-1 rounded-md bg-indigo-500/25 ring-1 ring-indigo-400/40"
                  style={{
                    left: `${Math.max(0, ((Math.min(inPoint, outPoint) - windowStart) / winDur) * 100)}%`,
                    width: `${Math.min(100, ((Math.abs(outPoint - inPoint) / winDur) * 100))}%`,
                  }}
                />
              ) : null}
              {markers.map((m) => {
                if (m.time_start_ms == null || m.time_end_ms == null) return null
                const startSec = m.time_start_ms / 1000
                const endSec = m.time_end_ms / 1000
                if (endSec < windowStart || startSec > winEnd) return null
                const left = ((Math.max(startSec, windowStart) - windowStart) / winDur) * 100
                const width =
                  ((Math.min(endSec, winEnd) - Math.max(startSec, windowStart)) / winDur) * 100
                const isPoint = m.time_start_ms === m.time_end_ms
                return (
                  <button
                    key={m.id}
                    type="button"
                    className={clsx(
                      'absolute top-1 bottom-1 z-10 rounded-sm',
                      isPoint ? 'w-0.5' : 'min-w-[2px]',
                      m.resolved ? 'bg-zinc-500/60' : 'bg-rose-400/80 hover:bg-rose-300',
                    )}
                    style={{ left: `${left}%`, width: isPoint ? undefined : `${Math.max(width, 0.4)}%` }}
                    title={formatReviewTimecode(startSec, fps)}
                    onClick={(e) => {
                      e.stopPropagation()
                      onMarkerClick?.(m)
                      seekToMs(m.time_start_ms!)
                    }}
                  />
                )
              })}
              <div
                className="absolute inset-y-0 z-20 w-0.5 -translate-x-1/2 bg-amber-300 shadow-[0_0_6px_rgba(251,191,36,0.8)]"
                style={{ left: `${Math.max(0, Math.min(100, playheadRatio * 100))}%` }}
              />
            </>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <Button type="button" size="sm" variant="ghost" className="h-8 px-2" onClick={() => seekTo(0)}>
            <ChevronFirst className="h-4 w-4" />
          </Button>
          <Button type="button" size="sm" variant="ghost" className="h-8 px-2" onClick={() => stepFrame(-1)}>
            <SkipBack className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="amber"
            className="h-8 px-3"
            onClick={togglePlay}
            disabled={!ready}
          >
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </Button>
          <Button type="button" size="sm" variant="ghost" className="h-8 px-2" onClick={() => stepFrame(1)}>
            <SkipForward className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 px-2"
            onClick={() => seekTo(duration)}
            disabled={duration <= 0}
          >
            <ChevronLast className="h-4 w-4" />
          </Button>

          <span className="mx-1 h-4 w-px bg-white/10" aria-hidden />

          <Button type="button" size="xs" variant="ghost" className="text-zinc-400" onClick={setInAtCurrent}>
            {t('reviews.videoIn')}
          </Button>
          <Button type="button" size="xs" variant="ghost" className="text-zinc-400" onClick={setOutAtCurrent}>
            {t('reviews.videoOut')}
          </Button>
          {inPoint != null || outPoint != null ? (
            <Button type="button" size="xs" variant="ghost" className="text-zinc-500" onClick={clearInOut}>
              {t('reviews.videoClearInOut')}
            </Button>
          ) : null}

          <span className="mx-1 h-4 w-px bg-white/10" aria-hidden />

          <Button
            type="button"
            size="xs"
            variant="ghost"
            className="text-zinc-400"
            disabled={videoZoomIdx <= 0}
            onClick={() => zoomVideo(-1)}
            title={t('reviews.videoZoomOut')}
          >
            <Minimize2 className="h-3.5 w-3.5" />
            <span className="sr-only">{t('reviews.videoZoomOut')}</span>
          </Button>
          <Button
            type="button"
            size="xs"
            variant="ghost"
            className="text-zinc-400"
            disabled={videoZoomIdx >= VIDEO_ZOOM_LEVELS.length - 1}
            onClick={() => zoomVideo(1)}
            title={t('reviews.videoZoomIn')}
          >
            <Maximize2 className="h-3.5 w-3.5" />
            <span className="sr-only">{t('reviews.videoZoomIn')}</span>
          </Button>
          <Button
            type="button"
            size="xs"
            variant="ghost"
            className="text-zinc-500"
            disabled={videoZoomIdx === 0 && pan.x === 0 && pan.y === 0}
            onClick={() => {
              setVideoZoomIdx(0)
              setPan({ x: 0, y: 0 })
            }}
          >
            {t('reviews.videoZoomReset')}
          </Button>

          {inPoint != null ? (
            <span className="ml-auto text-[10px] text-indigo-300/90">
              In {formatReviewTimecode(inPoint, fps)}
              {outPoint != null ? ` · Out ${formatReviewTimecode(outPoint, fps)}` : ''}
            </span>
          ) : null}
        </div>
      </div>
    )
  },
)
